/**
 * API utilities with timeout, retry, and abort handling
 *
 * Includes handling for:
 * - Request timeouts
 * - Network errors
 * - Stale connections after idle
 * - Automatic abort on component unmount
 */

const DEFAULT_TIMEOUT = 30000; // 30 seconds

// Track active requests so we can abort stale ones
const activeRequests = new Map<string, AbortController>();

export interface FetchOptions extends RequestInit {
  timeout?: number;
  requestId?: string; // Optional ID to track and cancel duplicate requests
}

/**
 * Cancel an active request by ID
 */
export function cancelRequest(requestId: string): void {
  const controller = activeRequests.get(requestId);
  if (controller) {
    controller.abort();
    activeRequests.delete(requestId);
  }
}

/**
 * Cancel all active requests
 * Useful when navigating away or logging out
 */
export function cancelAllRequests(): void {
  activeRequests.forEach((controller) => {
    controller.abort();
  });
  activeRequests.clear();
}

/**
 * Fetch with automatic timeout and abort handling
 * Returns null if the request times out or is aborted (instead of throwing)
 */
export async function fetchWithTimeout<T>(
  url: string,
  options: FetchOptions = {}
): Promise<{ data: T | null; error: string | null; aborted?: boolean }> {
  const { timeout = DEFAULT_TIMEOUT, signal: externalSignal, requestId, ...fetchOptions } = options;

  // Create abort controller for timeout
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  // Track this request if an ID was provided
  if (requestId) {
    // Cancel any existing request with the same ID
    cancelRequest(requestId);
    activeRequests.set(requestId, controller);
  }

  // If an external signal is provided, abort if it aborts
  if (externalSignal) {
    const externalAbortHandler = () => controller.abort();
    externalSignal.addEventListener('abort', externalAbortHandler);
  }

  try {
    const response = await fetch(url, {
      ...fetchOptions,
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    // Remove from active requests
    if (requestId) {
      activeRequests.delete(requestId);
    }

    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage = `HTTP ${response.status}`;
      try {
        const errorJson = JSON.parse(errorText);
        errorMessage = errorJson.error || errorJson.message || errorMessage;
      } catch {
        // Use status text if JSON parse fails
        errorMessage = response.statusText || errorMessage;
      }
      return { data: null, error: errorMessage };
    }

    const data = await response.json();
    return { data, error: null };
  } catch (err) {
    clearTimeout(timeoutId);

    // Remove from active requests
    if (requestId) {
      activeRequests.delete(requestId);
    }

    if (err instanceof Error) {
      if (err.name === 'AbortError') {
        // Request was aborted (either by timeout or external signal)
        return { data: null, error: null, aborted: true }; // Return null error for aborts (not a real error)
      }

      // Network errors (offline, DNS failure, etc.)
      if (err.message === 'Failed to fetch' || err.message.includes('NetworkError')) {
        return { data: null, error: 'Network error. Please check your connection.' };
      }

      return { data: null, error: err.message };
    }
    return { data: null, error: 'An unexpected error occurred' };
  }
}

/**
 * Get the API base URL
 */
export function getApiBaseUrl(): string {
  return (import.meta.env.VITE_API_URL || '').replace(/\/api\/?$/, '');
}

/**
 * Build API URL
 */
export function buildApiUrl(path: string): string {
  const base = getApiBaseUrl();
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `${base}/api${cleanPath}`;
}

/**
 * Create a fetch function that auto-cancels on component unmount
 * Usage:
 *   const { fetch, cancel } = createCancellableFetch();
 *   useEffect(() => cancel, []); // Auto-cancel on unmount
 */
export function createCancellableFetch() {
  const controller = new AbortController();

  return {
    fetch: async <T>(url: string, options: Omit<FetchOptions, 'signal'> = {}) => {
      return fetchWithTimeout<T>(url, {
        ...options,
        signal: controller.signal,
      });
    },
    cancel: () => controller.abort(),
    signal: controller.signal,
  };
}
